import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db, functions } from '../../firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    FormHelperText
} from '@mui/material';
import { toast } from 'react-toastify';

// Get a reference to our specific Cloud Function
const adminCreateStandardUserCallable = httpsCallable(functions, 'adminCreateStandardUser');

const CreateUserForm = ({ onUserCreated, onCancel }) => {
    const { isAdmin } = useAuth();

    // Form State
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [userType, setUserType] = useState('Advisor');
    const [accountType, setAccountType] = useState('Paid'); // NEW: Add state for account type
    const [assignedTemplateId, setAssignedTemplateId] = useState('');
    const [assignedAgreementId, setAssignedAgreementId] = useState('');

    // State for the dropdowns
    const [templates, setTemplates] = useState([]);
    const [agreements, setAgreements] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(true);
    const [loadingAgreements, setLoadingAgreements] = useState(true);
    const [organizations, setOrganizations] = useState([]); // NEW
    const [loadingOrganizations, setLoadingOrganizations] = useState(true); // NEW
     // Auto-populate organizationId from environment variable (CC instance = 'citrin_cooperman')
    const [organizationId, setOrganizationId] = useState(process.env.REACT_APP_ORGANIZATION_ID || '');
    const [claudeOpusFrequency, setClaudeOpusFrequency] = useState(0);
    // Submission and error state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Fetch active questionnaire templates for the dropdown
    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const q = query(
                    collection(db, 'questionnaireTemplates'),
                    where('status', '==', 'Active'),
                    orderBy('name', 'asc')
                );
                const querySnapshot = await getDocs(q);
                const templatesData = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setTemplates(templatesData);
            } catch (err) {
                console.error("Error fetching active templates:", err);
                toast.error("Could not load questionnaire templates.");
            } finally {
                setLoadingTemplates(false);
            }
        };

        fetchTemplates();
    }, []);

    // FIXED: Fetch real license agreements from Firestore (preserving all original functionality)
    useEffect(() => {
        const fetchAgreements = async () => {
            try {
                // Query real Firestore data instead of using placeholders
                const agreementsRef = collection(db, 'userAgreements');
                const q = query(agreementsRef, where('active', '==', true));
                const querySnapshot = await getDocs(q);
                
                const activeAgreements = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                
                setAgreements(activeAgreements);
                
            } catch (err) {
                console.error("Error fetching license agreements:", err);
                toast.error("Could not load license agreements.");
            } finally {
                setLoadingAgreements(false);
            }
        };

        fetchAgreements();
    }, []);

    // NEW: Fetch organizations for the dropdown
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const orgsQuery = query(collection(db, 'organizations'), orderBy('name', 'asc'));
        const querySnapshot = await getDocs(orgsQuery);
        const orgsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setOrganizations(orgsData);
      } catch (err) {
        console.error("Error fetching organizations:", err);
        toast.error("Could not load organizations.");
      } finally {
        setLoadingOrganizations(false);
      }
    };
    fetchOrganizations();
  }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!fullName || !email || !userType || !assignedTemplateId || !assignedAgreementId) {
            setError('All fields, including questionnaire template and license agreement, are required.');
            return;
        }

        if (!isAdmin) {
            toast.error("You do not have permission to create users.");
            return;
        }

        setIsSubmitting(true);
        console.log('DEBUG: The agreement ID being sent is:', assignedAgreementId);
        try {
            const result = await adminCreateStandardUserCallable({
                fullName,
                email,
                userType,
                accountType, // NEW: Pass the account type
                assignedQuestionnaireTemplateId: assignedTemplateId,
                assignedAgreementId: assignedAgreementId,
                claudeOpusFrequency: Number(claudeOpusFrequency) || 0,
                organizationId: organizationId || null // NEW: Pass the selected organizationId
  });

            if (result.data.success) {
                toast.success(result.data.message || `User ${email} created successfully!`);
                onUserCreated(); // Notify parent to refresh and close modal
            } else {
                throw new Error(result.data.error || 'An unexpected error occurred.');
            }
        } catch (err) {
            console.error("Error creating standard user:", err);
            const errorMessage = err.message || "An unexpected error occurred. Please check logs.";
            setError(errorMessage);
            toast.error(`Creation failed: ${errorMessage}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="h6">Create New Standard User</Typography>
            <Typography variant="body2" color="text.secondary">
                This creates a standard user (Advisor or CEO/Direct). The user will be created in Firebase Authentication, and an "Initial Client" will be generated for them automatically.
            </Typography>
           
            <TextField
                label="Full Name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                disabled={isSubmitting}
            />
            <TextField
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isSubmitting}
            />
            <FormControl fullWidth required disabled={isSubmitting}>
                <InputLabel>User Type</InputLabel>
                <Select
                    value={userType}
                    label="User Type"
                    onChange={(e) => setUserType(e.target.value)}
                >
                    <MenuItem value="Advisor">Advisor (Manages multiple clients)</MenuItem>
                    <MenuItem value="CEO/Direct">CEO/Direct (Single client user)</MenuItem>
                </Select>
            </FormControl>
            <FormControl fullWidth required disabled={isSubmitting}>
    <InputLabel>Account Type</InputLabel>
    <Select
        value={accountType}
        label="Account Type"
        onChange={(e) => setAccountType(e.target.value)}
    >
        <MenuItem value="Paid">Paid (Requires Subscription)</MenuItem>
        <MenuItem value="Free">Free (Internal/Trial)</MenuItem>
    </Select>
</FormControl>
{/* Assign to Organization (Content visibility in Firm Portal) */}
<FormControl fullWidth disabled={isSubmitting || loadingOrganizations}>
  <InputLabel>Assign to Organization (Optional)</InputLabel>
  <Select
    value={organizationId}
    label="Assign to Organization (Optional)"
    onChange={(e) => setOrganizationId(e.target.value)}
  >
    <MenuItem value=""><em>None</em></MenuItem>
    {loadingOrganizations ? (
      <MenuItem disabled><em>Loading firms...</em></MenuItem>
    ) : (
      organizations.map(org => (
        <MenuItem key={org.id} value={org.id}>
          {org.name}
        </MenuItem>
      ))
    )}
  </Select>
  <FormHelperText>User's content will appear in the firm's portal feed. Does not grant portal access.</FormHelperText>
</FormControl>
            <TextField
              fullWidth
              type="number"
              label="Claude Opus Frequency (Optional)"
              value={claudeOpusFrequency}
              onChange={(e) => setClaudeOpusFrequency(e.target.value)}
              helperText="Default is 0 (never use). 1 = always use, 3 = every 3rd question, etc."
              disabled={isSubmitting}
            />
            <FormControl fullWidth required disabled={isSubmitting || loadingTemplates}>
                <InputLabel>Assign Questionnaire Template</InputLabel>
                <Select
                    value={assignedTemplateId}
                    label="Assign Questionnaire Template"
                    onChange={(e) => setAssignedTemplateId(e.target.value)}
                >
                    {loadingTemplates ? (
                        <MenuItem disabled><em>Loading templates...</em></MenuItem>
                    ) : (
                        templates.map(template => (
                            <MenuItem key={template.id} value={template.id}>
                                {template.name} (v{template.version})
                            </MenuItem>
                        ))
                    )}
                </Select>
            </FormControl>

            {/* FIXED: License Agreement Dropdown - Now uses real Firestore data */}
            <FormControl fullWidth required disabled={isSubmitting || loadingAgreements}>
                <InputLabel>Assign License Agreement</InputLabel>
                <Select
                    value={assignedAgreementId}
                    label="Assign License Agreement"
                    onChange={(e) => setAssignedAgreementId(e.target.value)}
                >
                    {loadingAgreements ? (
                        <MenuItem disabled><em>Loading agreements...</em></MenuItem>
                    ) : agreements.length === 0 ? (
                        <MenuItem disabled><em>No active agreements found</em></MenuItem>
                    ) : (
                        agreements.map(agreement => (
                            <MenuItem key={agreement.id} value={agreement.id}>
                                {agreement.title}
                            </MenuItem>
                        ))
                    )}
                </Select>
            </FormControl>

            {error && <Alert severity="error">{error}</Alert>}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                <Button onClick={onCancel} disabled={isSubmitting}>Cancel</Button>
                <Button type="submit" variant="contained" disabled={isSubmitting}>
                    {isSubmitting ? <CircularProgress size={24} /> : 'Create User'}
                </Button>
            </Box>
        </Box>
    );
};

export default CreateUserForm;